import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PDFLeadMagnet from '../components/PDFLeadMagnet.jsx';

function BoundaryService() {
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cream-50">
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6 leading-tight">
            Peoplepleasing No More Individual Coaching
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            Through secure online coaching sessions, I work with clients worldwide—helping you learn to say no with confidence, set healthy boundaries in all your relationships, and honor your needs while staying connected to others.
          </p>
        </div>
      </section>

      {/* Image */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-5xl mx-auto">
          <img 
            src="https://res.cloudinary.com/makerinc/image/fetch/c_limit,w_1200,f_auto,q_auto:best,fl_preserve_transparency,dpr_2/https%3A%2F%2Fmaker-devi-dev.s3-accelerate.amazonaws.com%2Fuploads%2Fuser_33tbl7B6e1bXN6KW4wjrevvqrth%2Fk6madjzd5jwco436pgy5kok0%2Fmessages%2Funtitled-design-g5i1msg796d.png"
            alt="Warm woman with glasses smiling joyfully"
            className="w-full h-[500px] object-cover rounded-2xl shadow-lg"
            style={{ objectPosition: 'center 20%' }}
          />
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-6">Your Body Already Knows How to Set Boundaries</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Most of us have been taught that boundaries are about finding the right words... assertive statements, clear declarations, firm responses. But here's what I've learned in my work as a somatic psychologist: your body has been setting boundaries since the moment you were born. Through sensation. Through energy. Through the ancient wisdom of your nervous system.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              The problem isn't that you don't know how to set boundaries. The problem is that you've been conditioned to ignore the ones your body is already setting.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Every time you feel that tightness in your chest but say yes anyway. Every time your jaw clenches but you force a smile instead. Every time you notice that heavy, sinking feeling and dismiss it as "just anxiety"... when really, it's your body's intelligence trying to speak to you.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              In our "good vibes only" culture, we've been trained to override discomfort, to choose forced positivity over authentic feeling. We've learned to survive by disconnecting from our body's natural boundary system. But survival isn't the same as thriving.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Here's what I know to be true: Your body holds ancient wisdom about safety, about connection, about what serves you and what doesn't. Those uncomfortable sensations aren't inconveniences to be pushed through or fixed. They're messengers, guiding you back home to yourself.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Real boundaries don't start with finding the perfect words. They start with turning toward the discomfort instead of away from it. With staying present with sensation. With trusting the intelligence that's been living in your body all along.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              <strong>Your body knows. The question is: are you ready to listen?</strong>
            </p>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">Is This For You?</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              Boundary coaching might be right for you if you:
            </p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Say yes when you really want to say no
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Feel guilty or anxious when you prioritize your own needs
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Overcommit to family, friends, or work and feel burned out
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Struggle to speak up when something bothers you
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Feel resentful after agreeing to things you didn't want to do
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Have trouble identifying what you actually want or need
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Let others take advantage of your kindness or generosity
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-softblue-600 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Fear that setting boundaries will damage your relationships
              </li>
            </ul>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">What We'll Work On Together</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Boundary coaching is practical, actionable, and deeply personal. We won't just talk about boundaries in theory—we'll practice them together in real-life scenarios so you feel confident applying them in your daily life.
            </p>

            <div className="space-y-6 mb-8">
              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Identifying Your Boundaries</h3>
                <p className="text-gray-600 leading-relaxed">
                  Before you can set a boundary, you need to know what you need. We'll work on tuning into your feelings, identifying when your boundaries are being crossed, and getting clear on what matters most to you.
                </p>
              </div>

              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Saying No (Even When Guilt Shows Up)</h3>
                <p className="text-gray-600 leading-relaxed">
                  You'll learn practical scripts and language to say no clearly and kindly—without over-explaining, justifying, or apologizing. We'll practice together so you feel confident when the moment comes.
                </p>
              </div>

              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Navigating Pushback</h3>
                <p className="text-gray-600 leading-relaxed">
                  Not everyone will like your boundaries at first—and that's okay. We'll prepare you for how to handle resistance, manipulation, or guilt trips, so you can stay grounded in your decision.
                </p>
              </div>

              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Setting Boundaries in Different Relationships</h3>
                <p className="text-gray-600 leading-relaxed">
                  Boundaries look different with family, friends, romantic partners, and coworkers. We'll customize your boundary-setting approach for each relationship type so you feel prepared in any situation.
                </p>
              </div>

              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Building Confidence and Self-Advocacy</h3>
                <p className="text-gray-600 leading-relaxed">
                  Setting boundaries is an act of self-advocacy. We'll work on building your confidence so you trust your own voice, honor your needs, and show up authentically without fear of judgment or rejection.
                </p>
              </div>

              <div className="bg-cream-50 p-6 rounded-xl border border-cream-200">
                <h3 className="text-xl font-medium text-sage-800 mb-3">Maintaining Boundaries Long-Term</h3>
                <p className="text-gray-600 leading-relaxed">
                  Setting a boundary once isn't enough—you need to maintain it. We'll develop strategies to stay consistent, reinforce your boundaries when they're tested, and adjust them as your needs change.
                </p>
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">My Approach to Boundary Coaching</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              I believe boundaries are learned through practice, not perfection. In our work together, you'll get:
            </p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span><strong>Real-life scripts and language</strong> to use in different situations</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span><strong>Role-playing and practice</strong> to build confidence before tough conversations</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span><strong>Support navigating guilt and anxiety</strong> that come up when setting boundaries</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span><strong>Personalized strategies</strong> tailored to your specific relationships and challenges</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span><strong>Accountability and follow-up</strong> as you practice in real life</span>
              </li>
            </ul>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              This isn't just theory—it's actionable, hands-on work that you can immediately apply to your life. You'll leave each session with clarity, tools, and confidence to take the next step.
            </p>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">What Makes Boundary Coaching Different?</h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Unlike traditional therapy (which focuses on deeper exploration and healing), boundary coaching is action-oriented and skills-focused. It's perfect if you:
            </p>
            <ul className="space-y-3 text-gray-600 mb-8">
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span>Want practical tools and strategies you can use immediately</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span>Need support with a specific boundary challenge or relationship</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span>Prefer a shorter-term, goal-focused approach</span>
              </li>
              <li className="flex items-start">
                <span className="text-softblue-600 mr-3 font-medium">•</span>
                <span>Want accountability and real-time feedback as you practice</span>
              </li>
            </ul>

            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              That said, boundary coaching can also be integrated with therapy if you're working on deeper issues like people-pleasing, codependency, or trauma. We'll tailor our approach to what you need most.
            </p>

            <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mt-16 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-6 mb-12">
              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">Are all coaching sessions online?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! All coaching sessions are conducted online via HIPAA compliant secure video conferencing. I work with coaching clients worldwide, making it easy to fit boundary work into your busy schedule from anywhere in the world.
                </p>
              </div>

              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">How long does boundary coaching take?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Many clients see progress within 4-8 sessions, especially if they're focused on a specific challenge. Others continue longer for ongoing support and deeper work. It depends on your goals and needs.
                </p>
              </div>

              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">Will setting boundaries ruin my relationships?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Healthy relationships will adjust and often grow stronger when you set boundaries. Relationships that rely on you having no boundaries may shift—but that's actually a sign that the relationship wasn't built on mutual respect. You deserve relationships where you can be yourself.
                </p>
              </div>

              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">What if I feel too guilty to set boundaries?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Guilt is normal, especially at first. We won't try to eliminate it—instead, we'll learn to acknowledge the guilt and move forward anyway. Over time, as you see the positive impact of boundaries, the guilt tends to lessen.
                </p>
              </div>

              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">Can I do boundary coaching online?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes! All sessions are conducted via secure video call, making it easy and convenient to work with me from anywhere. This also allows you to practice boundaries in your real-life environment.
                </p>
              </div>

              <div className="bg-white border-l-4 border-softblue-400 p-6 rounded-r-xl">
                <h3 className="text-xl font-medium text-sage-800 mb-2">What if I've tried setting boundaries before and failed?</h3>
                <p className="text-gray-600 leading-relaxed">
                  "Failing" at boundaries often happens because we lack the tools, support, or confidence to maintain them. Together, we'll identify what got in the way before and build a plan that actually works for you. You won't be doing this alone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Section - Hidden for now, will add later */}
      {/* <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-800 mb-8 text-center">Additional Resources</h2>
          
          <div className="bg-gradient-to-br from-warm-50 to-sage-50 rounded-2xl p-8 mb-8 border border-warm-100">
            <h3 className="text-2xl font-semibold text-sage-800 mb-4">People-Pleasing in the Therapy Room</h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Wondering if you're people-pleasing your therapist? Explore how power dynamics, privilege, and cultural differences shape the therapeutic relationship—and what to look for in a culturally humble therapist who understands intersectional identities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/therapy-relationship"
                className="inline-block bg-warm-600 text-white px-6 py-3 rounded-lg text-base font-medium hover:bg-warm-700 transition-colors text-center"
              >
                Read the Full Article
              </Link>
              <button 
                onClick={() => setIsPDFModalOpen(true)}
                className="inline-block bg-sage-600 text-white px-6 py-3 rounded-lg text-base font-medium hover:bg-sage-700 transition-colors text-center"
              >
                Download Free PDF
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* Group Coaching Cross-Promotion */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 bg-white border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-warm-50 to-sage-50 rounded-2xl p-8 md:p-10 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <span className="inline-block px-3 py-1 bg-warm-500 text-white rounded-full text-xs font-semibold uppercase tracking-wide">
                🎄 Starting November 20th
              </span>
            </div>
            <h3 className="text-2xl md:text-3xl font-serif font-light text-sage-800 mb-4">
              Practice Boundaries in Our Group Program
            </h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Want to practice boundary-setting with others? Join our ongoing group coaching program where you'll learn to honor your body's no, regulate your nervous system, and build authentic connections in a safe, supportive space.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-warm-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Thursdays 11am-1pm PST</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-warm-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">$249/month</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-warm-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">25 topics with lifetime materials</span>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-warm-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">Perfect timing for the holidays</span>
              </div>
            </div>
            <Link 
              to="/services/group-coaching"
              className="inline-block bg-warm-500 text-white px-6 py-3 rounded-full text-base font-semibold hover:bg-warm-600 transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Learn More About Group Coaching
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-sage-50 to-warm-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-light text-sage-800 mb-6">
            Ready to Honor Your Needs?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Book a free 15-minute consultation to see if we're a good fit. No pressure, no obligation—just a conversation about how I can support you.
          </p>
          <Link 
            to="/#booking"
            className="inline-block bg-warm-500 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-warm-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Schedule Your Free Consultation
          </Link>
        </div>
      </section>

      {/* PDF Lead Magnet Modal */}
      <PDFLeadMagnet 
        isOpen={isPDFModalOpen} 
        onClose={() => setIsPDFModalOpen(false)} 
      />
    </div>
  );
}

export default BoundaryService;
